import axios from "axios";

import { getToken } from "../helpers/localStorageCache";
import { getNetworkViewLocationId } from "../helpers/networkViewLocation";
import { makeSearchParams } from "../helpers/string";
import MOCK_DATA from "../constants/mockUserData";
import { LOCALSTORAGE_KEYS } from "../constants/users";
import { reportCancelledItem } from "../helpers/cancelledItemAlert";

/**
 * To debug any sub domain
 * set debugginMode to TRUE
 * change x-uid and host name to that of the reuqired tenant
 */
export const debuggingMode = false;
export const mockUserAndDomain = MOCK_DATA["taylor"];
export const getUID = () =>
  debuggingMode
    ? mockUserAndDomain?.uid
    : `${getToken(LOCALSTORAGE_KEYS.authUser)?.uid}`;
export const getMockUID = () =>
  debuggingMode
    ? mockUserAndDomain?.uid
    : `${getToken(LOCALSTORAGE_KEYS.mockUser)?.uid}`;
export const getDomainHostname = () => {
  if (debuggingMode) return mockUserAndDomain?.domain;
  const host =
    typeof window !== "undefined" ? window.location.hostname : "";
  const mockHost = getToken(LOCALSTORAGE_KEYS.mockUser)?.currentDomain;
  if (
    mockHost &&
    (host === "localhost" || host === "127.0.0.1")
  ) {
    return mockHost;
  }
  return host;
};
export const getMockDomainHostname = () =>
  debuggingMode
    ? mockUserAndDomain?.domain
    : `${getToken(LOCALSTORAGE_KEYS.mockUser)?.currentDomain}`;

// api.riverr.app fronts the Node API on Cloud Run (service riverr-api) via a
// global HTTPS load balancer; App Engine `default` keeps serving the appspot
// hostname for callers not yet repointed. Rollback is this one line.
export let nodeServer = "https://api.riverr.app";
export let pythonServer =
  "https://riverr-enterprise-integrations-dot-riverr-enterprise-user.uc.r.appspot.com";

// The GraphQL `httpLink` moved to `src/graphQL/index.js` (the lazily-loaded
// Apollo client) so `@apollo/client` no longer ships in `main.js` via this
// always-loaded API module.

// Opt-in only. Default localhost hits production App Engine so a mock
// user (e.g. brad@bespokethreads) loads that tenant. Set
// REACT_APP_LOCAL_API=1 to talk to nodemon :4000 instead.
if (
  typeof window !== "undefined" &&
  process.env.REACT_APP_LOCAL_API === "1" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
) {
  nodeServer = "//localhost:4000";
}

// Our APIs verify the signed-in person from this Firebase ID token (x-uid
// only names the account to act as). Sent only to the Node and Python APIs,
// never to third party URLs. Loaded lazily so ajax.js stays light.
const loginTokenHeader = async () => {
  try {
    const { auth } = await import("../helpers/firebase_helper");
    if (typeof auth?.authStateReady === "function") await auth.authStateReady();
    const user = auth?.currentUser;
    if (!user) return {};
    return { "x-id-token": await user.getIdToken() };
  } catch (e) {
    return {};
  }
};

const isOurApiUrl = (url) =>
  typeof url === "string" &&
  (url.startsWith(nodeServer) ||
    url.startsWith(pythonServer) ||
    url.startsWith("https://riverr-enterprise-user.uc.r.appspot.com"));

const clientUrl = (uri, server) => {
  let uriPath = uri;

  const pattern = /^https?:/;
  if (!pattern.test(uri)) {
    uriPath = uri.startsWith("/") ? uri : `/${uri}`;

    if (server === "python") {
      return `${pythonServer}${uriPath}`;
    }

    return `${nodeServer}${uriPath}`;
  }

  return uriPath;
};

const objectToFormData = (obj, form, namespace) => {
  const fd = form || new FormData();
  let formKey;
  const objectKeys = Object.keys(obj);

  objectKeys.forEach((property) => {
    if (namespace) {
      formKey = `${namespace}[${property}]`;
    } else {
      formKey = property;
    }

    const value = obj[property];

    if (Array.isArray(value)) {
      // value is an Array
      arrayToFormData(fd, formKey, value); // eslint-disable-line no-use-before-define
    } else if (typeof value === "object" && !(value instanceof File)) {
      // value is a nested object
      objectToFormData(value, fd, formKey);
    } else {
      // value is a string or a File object
      fd.append(formKey, value);
    }
  });

  return fd;
};

const arrayToFormData = (fd, formKey, value) => {
  value.forEach((a) => {
    if (typeof a === "object" && !(a instanceof File)) {
      // elements of array is nested object
      objectToFormData(value, fd, `${formKey}[]`);
    } else {
      // elements of array is string or File object
      fd.append(`${formKey}[]`, a);
    }
  });
};

const buildParam = (params, asJSON = true) => {
  if (asJSON) {
    return JSON.stringify(params);
  }
  return objectToFormData(params);
};

const getDefaultOptions = () => {
  const uid = getUID();
  const domainHostname = getDomainHostname();
  const mockUid = getMockUID();
  const mockDomainHostname = getMockDomainHostname();

  let headers = {
    Accept: "application/json",
    "domain-hostname": domainHostname,
  };

  if (uid && uid !== "undefined") {
    headers = {
      ...headers,
      "x-uid": uid,
    };
  }

  if (mockUid && mockUid !== "undefined") {
    headers = {
      ...headers,
      "x-uid": mockUid,
      "mock-uid": mockUid,
    };
  }

  if (mockDomainHostname && mockDomainHostname !== "undefined") {
    headers = {
      ...headers,
      "domain-hostname": mockDomainHostname,
      "mock-domain-hostname": mockDomainHostname,
    };
  }

  const viewLocationId = getNetworkViewLocationId();
  if (viewLocationId) {
    headers = {
      ...headers,
      "x-view-location-id": viewLocationId,
    };
  }

  try {
    const stationToken =
      typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem("rps.stationToken")
        : null;
    if (stationToken && (!headers["x-uid"] || headers["x-uid"] === "undefined")) {
      headers = {
        ...headers,
        "x-station-token": stationToken,
      };
    }
  } catch {
    /* private mode */
  }

  return {
    method: "GET",
    headers,
  };
};

function ajax(uri, options = {}) {
  let pathString = uri;
  let defaultOptions = getDefaultOptions();

  defaultOptions = {
    ...defaultOptions,
    method: options.method || defaultOptions.method,
  };

  if (options.responseType) {
    defaultOptions.responseType = options.responseType;
  }

  if (options.data) {
    if (
      typeof FormData !== "undefined" &&
      options.data instanceof FormData
    ) {
      // Let the browser set multipart boundary — do not force Content-Type.
      defaultOptions.data = options.data;
    } else if (options.formData) {
      defaultOptions.data = buildParam(options.data, false);
    } else {
      defaultOptions.headers["Content-Type"] =
        "application/json; charset=UTF-8";
      defaultOptions.data = buildParam(options.data);
    }
  }

  if (options.headers) {
    // Merge so Authorization does not drop the x-uid ajax already set.
    defaultOptions.headers = {
      ...defaultOptions.headers,
      ...options.headers,
    };
  }

  if (options.query) {
    pathString = `${pathString}?${makeSearchParams(options.query)}`;
  }

  if (options.timeout != null) {
    defaultOptions.timeout = options.timeout;
  }

  if (options.signal) {
    defaultOptions.signal = options.signal;
  }

  // Byte-level progress for large uploads (print files can be 10–50 MB).
  if (typeof options.onUploadProgress === "function") {
    defaultOptions.onUploadProgress = options.onUploadProgress;
  }

  if (options.skipAuth) {
    delete defaultOptions.headers["x-uid"];
    delete defaultOptions.headers["mock-uid"];
  }

  // Replay a queued handheld scan as the person who scanned it, even if
  // someone else is signed in when the radio comes back.
  if (options.asUserId) {
    defaultOptions.headers["x-uid"] = String(options.asUserId);
  }

  defaultOptions.url = clientUrl(pathString, options.server);

  // A queued scan replays as the person who scanned it, so it must not carry
  // the current person's token; skipAuth calls are anonymous on purpose.
  if (
    !options.skipAuth &&
    !options.asUserId &&
    isOurApiUrl(defaultOptions.url)
  ) {
    return loginTokenHeader().then((tokenHeader) => {
      defaultOptions.headers = { ...defaultOptions.headers, ...tokenHeader };
      return withCancelledItemAlert(axios(defaultOptions));
    });
  }

  return withCancelledItemAlert(axios(defaultOptions));
}

// A scan of an item pulled from production opens the global popup, then the
// caller still gets the error as before.
function withCancelledItemAlert(request) {
  return request.catch((error) => {
    try {
      reportCancelledItem(error);
    } catch {
      /* the popup must never change what the caller sees */
    }
    return Promise.reject(error);
  });
}

export default ajax;
